// Real US stocks via Finnhub polling + synth candles anchored to real prices
import { synthFetchKlines } from './synthMarket';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STOCK_ASSETS = [
  { symbol: 'AAPL',  display: 'AAPL',  pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 195 },
  { symbol: 'TSLA',  display: 'TSLA',  pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 245 },
  { symbol: 'MSFT',  display: 'MSFT',  pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 425 },
  { symbol: 'GOOGL', display: 'GOOGL', pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 175 },
  { symbol: 'AMZN',  display: 'AMZN',  pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 185 },
  { symbol: 'NVDA',  display: 'NVDA',  pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 125 },
  { symbol: 'META',  display: 'META',  pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 510 },
  { symbol: 'JPM',   display: 'JPM',   pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 205 },
  { symbol: 'V',     display: 'V',     pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 275 },
  { symbol: 'SPY',   display: 'SPY',   pricePrecision: 2, class: 'stocks', provider: 'stocks', base: 520 }
];

const stockCache = {}; // symbol -> { last, open, high, low, prevClose, changePct, candles }
const stockListeners = {}; // symbol -> { ticker: [], kline: {}, depth: [], trades: [] }
let pollerStarted = false;

async function fetchLiveQuotes(symbols) {
  const url = `${BASE}/finnhub/quotes?symbols=${symbols.join(',')}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('quote fetch failed');
  return res.json();
}

function ensureStock(symbol) {
  if (stockCache[symbol]) return stockCache[symbol];
  const asset = STOCK_ASSETS.find(a => a.symbol === symbol);
  const st = { last: asset?.base || 100, open: asset?.base || 100, high: 0, low: 0, prevClose: asset?.base || 100, changePct: 0, candles: {} };
  stockCache[symbol] = st;
  stockListeners[symbol] = { ticker: [], kline: {}, depth: [], trades: [] };
  return st;
}

function startPoller() {
  if (pollerStarted) return;
  pollerStarted = true;
  const pollOnce = async () => {
    const subs = Object.keys(stockListeners).filter(s => stockListeners[s].ticker.length + Object.keys(stockListeners[s].kline).length > 0);
    if (subs.length === 0) return;
    try {
      const quotes = await fetchLiveQuotes(subs);
      const now = Math.floor(Date.now() / 1000);
      for (const sym of subs) {
        const q = quotes[sym];
        if (!q || !q.c) continue;
        const st = stockCache[sym];
        st.last = q.c;
        st.open = q.o || st.open;
        st.high = q.h || st.high;
        st.low = q.l || st.low;
        st.prevClose = q.pc || st.prevClose;
        st.changePct = q.dp || 0;
        // Notify ticker listeners
        const t = {
          symbol: sym, last: q.c, changePct: q.dp || 0,
          change: q.d || 0, high: q.h, low: q.l,
          volume: 0, quoteVolume: 0,
          bid: q.c - 0.01, ask: q.c + 0.01
        };
        stockListeners[sym].ticker.forEach(cb => cb(t));
        // Update last candle for each interval
        for (const iv of Object.keys(stockListeners[sym].kline)) {
          const ivSec = { '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400, '1d': 86400 }[iv] || 60;
          const barStart = Math.floor(now / ivSec) * ivSec;
          const arr = st.candles[iv] || [];
          let last = arr[arr.length - 1];
          if (!last || last.time < barStart) {
            last = { time: barStart, open: q.c, high: q.c, low: q.c, close: q.c, volume: 0 };
            arr.push(last);
            st.candles[iv] = arr;
          } else {
            last.close = q.c;
            if (q.c > last.high) last.high = q.c;
            if (q.c < last.low) last.low = q.c;
          }
          (stockListeners[sym].kline[iv] || []).forEach(cb => cb({ ...last, closed: false }));
        }
      }
    } catch (e) { /* silent */ }
  };
  setInterval(pollOnce, 5000);
  pollOnce();
}

export async function stocksFetchKlines(symbol, interval, limit = 300) {
  ensureStock(symbol);
  const st = stockCache[symbol];
  // Get synth candle history (deterministic) then re-anchor to current price
  const synthCandles = await synthFetchKlines(symbol + '_STOCK', interval, limit).catch(() => []);
  let candles = synthCandles;
  if (!candles || candles.length === 0) {
    // Fallback: generate simple candles around st.last
    const now = Math.floor(Date.now() / 1000);
    const ivSec = { '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400, '1d': 86400 }[interval] || 60;
    candles = [];
    let p = st.last * 0.99;
    for (let i = limit; i > 0; i--) {
      const time = now - i * ivSec;
      const move = (Math.random() - 0.5) * st.last * 0.005;
      const open = p;
      const close = p + move;
      const high = Math.max(open, close) + Math.random() * st.last * 0.003;
      const low = Math.min(open, close) - Math.random() * st.last * 0.003;
      candles.push({ time, open, high, low, close, volume: 1000 + Math.random() * 5000 });
      p = close;
    }
  }
  // Anchor last candle to real price
  if (candles.length && st.last) {
    const last = candles[candles.length - 1];
    const diff = st.last - last.close;
    // shift all candles by fraction of diff to smoothly anchor
    for (let i = candles.length - 30; i < candles.length; i++) {
      if (i < 0) continue;
      const w = (i - (candles.length - 30)) / 30;
      candles[i].open += diff * w;
      candles[i].close += diff * w;
      candles[i].high += diff * w;
      candles[i].low += diff * w;
    }
    last.close = st.last;
    if (last.close > last.high) last.high = last.close;
    if (last.close < last.low) last.low = last.close;
  }
  st.candles[interval] = candles;
  return candles;
}

export async function stocksFetch24hr(symbol) {
  ensureStock(symbol);
  const st = stockCache[symbol];
  // Trigger fetch
  try {
    const q = (await fetchLiveQuotes([symbol]))[symbol];
    if (q && q.c) {
      st.last = q.c; st.open = q.o || q.c; st.high = q.h || q.c; st.low = q.l || q.c;
      st.prevClose = q.pc || q.c; st.changePct = q.dp || 0;
    }
  } catch {}
  return {
    lastPrice: st.last,
    priceChangePercent: st.changePct,
    highPrice: st.high,
    lowPrice: st.low,
    quoteVolume: 0,
    bidPrice: st.last - 0.01,
    askPrice: st.last + 0.01
  };
}

export async function stocksFetchDepth(symbol) {
  ensureStock(symbol);
  const st = stockCache[symbol];
  const bids = [], asks = [];
  for (let i = 0; i < 20; i++) {
    bids.push([(st.last - 0.01 - i * 0.02).toString(), (10 + Math.random() * 90).toString()]);
    asks.push([(st.last + 0.01 + i * 0.02).toString(), (10 + Math.random() * 90).toString()]);
  }
  return { bids, asks };
}

export function stocksSubscribeKline(symbol, interval, onMsg) {
  ensureStock(symbol);
  stockListeners[symbol].kline[interval] = stockListeners[symbol].kline[interval] || [];
  stockListeners[symbol].kline[interval].push(onMsg);
  startPoller();
  return { close: () => {
    const arr = stockListeners[symbol].kline[interval] || [];
    const i = arr.indexOf(onMsg);
    if (i >= 0) arr.splice(i, 1);
  } };
}

export function stocksSubscribeTicker(symbol, onMsg) {
  ensureStock(symbol);
  stockListeners[symbol].ticker.push(onMsg);
  startPoller();
  return { close: () => {
    const i = stockListeners[symbol].ticker.indexOf(onMsg);
    if (i >= 0) stockListeners[symbol].ticker.splice(i, 1);
  } };
}

export function stocksSubscribeDepth(symbol, onMsg) {
  ensureStock(symbol);
  // Fake depth updates every 2s using latest price
  const iv = setInterval(async () => {
    const d = await stocksFetchDepth(symbol);
    onMsg({
      bids: d.bids.map(([p, q]) => ({ price: parseFloat(p), qty: parseFloat(q) })),
      asks: d.asks.map(([p, q]) => ({ price: parseFloat(p), qty: parseFloat(q) }))
    });
  }, 2000);
  return { close: () => clearInterval(iv) };
}

export function stocksSubscribeTrades(symbol, onMsg) {
  ensureStock(symbol);
  const iv = setInterval(() => {
    const st = stockCache[symbol];
    if (!st.last) return;
    const buyerMaker = Math.random() < 0.5;
    onMsg({ id: Date.now() + Math.random(), price: st.last + (Math.random() - 0.5) * 0.05, qty: 10 + Math.floor(Math.random() * 100), time: Date.now(), buyerMaker });
  }, 1500);
  return { close: () => clearInterval(iv) };
}

export function stocksSubscribeMulti(symbols, onMsg) {
  const closers = symbols.map(s => stocksSubscribeTicker(s, onMsg));
  return { close: () => closers.forEach(c => c.close()) };
}
